import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/TopBar";
import IMAGES from "../../../../constant/images";
import { Button } from "antd";
import { Plus } from "lucide-react";
import PractitionerCounter from "../../components/PractitionerCounter";
import PractitionerTable from "../../components/PractitionerTable";
import PractitionerControl from "../../components/PractitionerControl";
import PractitionerFilterSlider from "../../components/Practitioner/PractitionerFilterSlider";
import { transformPractitionerFiltersForAPI } from "../../../../utils";
import useIsMobile from "../../components/shared/Mobile-Responsive";
import ExportModal from "../../components/shared/ExportModal";
import api from "../../../../redux/instance/api";
import { getPractitionerStats } from "../../api/Practitioner/index";

// Mapping stat card IDs to API filter values
const STAT_TO_FILTER_MAP = {
  'total': 'all',
  'available': 'available_today'
};

export default function Index() {
  const [collapsed, setCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedStatId, setSelectedStatId] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  // Load practitioner statistics on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(getPractitionerStats());
    }
  }, [dispatch]);

  // Handle stat card click for filtering
  const handleStatCardClick = useCallback((statId) => {
    if (selectedStatId === statId) {
      // Deselect if clicking the same stat
      setSelectedStatId(null);
      setAppliedFilters(null);
    } else {
      // Select new stat and apply filter
      setSelectedStatId(statId);
      const filterValue = STAT_TO_FILTER_MAP[statId];

      if (filterValue === 'all') {
        setAppliedFilters(null);
      } else if (filterValue === 'available_today') {
        // Use the same filter structure as the filter slider
        setAppliedFilters({ availability: ['Available Today'] });
      }
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [selectedStatId]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterApply = (filters) => {
    setAppliedFilters(filters);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleFilterClear = () => {
    setAppliedFilters(null);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handleSortChange = (sortData) => {
    setSortConfig(sortData);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const fetchAllPractitioners = useCallback(async () => {
    const combinedFilters = {};

    if (searchTerm && searchTerm.trim()) {
      combinedFilters.name = searchTerm.trim();
    }

    if (appliedFilters) {
      const transformedFilters = transformPractitionerFiltersForAPI(appliedFilters);
      Object.assign(combinedFilters, transformedFilters);
    }

    const params = new URLSearchParams({
      page: "0",
      size: "5000"
    });

    const FILTER_PARAMS = ['specialization', 'status', 'name'];
    FILTER_PARAMS.forEach(filterKey => {
      if (combinedFilters[filterKey]) {
        params.append(filterKey, combinedFilters[filterKey]);
      }
    });

    if (combinedFilters.availableToday !== undefined) {
      params.append('availableToday', combinedFilters.availableToday.toString());
    }

    const response = await api.get(`/admin/counsellors-list?${params.toString()}`);
    const payload = response.data?.payload;
    const list = payload?.data || [];

    return list.map((item, index) => ({
      key: item.pcUserId || `practitioner-${index}`,
      pcUserId: item.pcUserId,
      name: item.name,
      specialization: item.specialization,
      languages: item.languages,
      availableDays: item.availableDays,
      status: item.status,
      numberOfConsultations: item.numberOfConsultations || 0,
    }));
  }, [searchTerm, appliedFilters]);

  const handleExportClick = () => {
    setExportModalVisible(true);
  };

  const handleCloseModal = () => {
    setExportModalVisible(false);
  };

  const handleExportData = (format) => {
    console.log(`Exporting practitioner data as ${format}`);
    setExportModalVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 flex bg-[#F8FAFC] overflow-hidden">
      <TopBar />

      {/* Sidebar */}
      <div
        className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}
      >
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div
        className={`flex-1 min-w-0 overflow-auto bg-[#F8FAFC] pt-2 px-2 pb-8 transition-all duration-300 ${
          collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"
        }`}
      >
        <div className="p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl">
          {/* Header */}
          {isMobile ? (
            <>
             <div className="flex items-center justify-between gap-2 p-2 mb-1">
          <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Practitioners</h1>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleExportClick}
              className="flex items-center gap-2 h-9 px-[14px] py-0 bg-white text-[#171717] text-[13px] font-semibold border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition hover:text-[#171717]"
            >
              Export
              <img src={IMAGES.export_icon} alt="export_icon" className="w-[16px]" />
            </Button>

            <Button
              onClick={() => (window.location.href = "/add-practitioner")}
              className="btn-gradient flex items-center justify-center h-9 px-[10px] py-0 rounded-xl border-0"
            >
              <Plus size={16} />
            </Button>
          </div>
        </div>
            </>
          ):(
            <>
             <div className="w-full mx-auto p-2 mb-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Practitioners</h1>

              <div className="flex gap-2">
                <Button
                  onClick={handleExportClick}
                  className="flex items-center gap-2 h-9 px-[14px] py-0 bg-white text-[#171717] text-[13px] font-semibold border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition hover:text-[#171717]"
                >
                  Export
                  <img src={IMAGES.export_icon} alt="export_icon" className="w-[16px]" />
                </Button>

                <Button
                  onClick={() => (window.location.href = "/add-practitioner")}
                  type="primary"
                  className="btn-gradient flex items-center gap-2 h-9 px-[14px] py-0 text-[13px] font-semibold rounded-xl border-0 transition"
                >
                  <Plus size={16} />
                  Add Practitioner
                </Button>
              </div>
            </div>
          </div>
            </>
          )}
          <div className="flex flex-col lg:flex-row gap-3 p-2">
            {/* Left Side */}
            <div className="flex-1">
              <div className="mt-0">
                <PractitionerCounter
                  onStatClick={handleStatCardClick}
                  selectedStatId={selectedStatId}
                />
              </div>
            </div>
          </div>
          <PractitionerControl
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSearch={handleSearch}
            onFilterToggle={handleFilterToggle}
            onSortChange={handleSortChange}
          />
          <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9] mt-0 mb-1" />
          <PractitionerTable
            searchTerm={searchTerm}
            appliedFilters={appliedFilters}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onSelectedRowsChange={(rows) => setSelectedRows(rows)}
            setExportData={setExportData}
            selectedRows={selectedRows}
            sortConfig={sortConfig}
          />

          {/* Filter Slider */}
          <PractitionerFilterSlider
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilters={handleFilterApply}
            onClearAll={handleFilterClear}
          />
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        visible={exportModalVisible}
        onClose={handleCloseModal}
        onExport={handleExportData}
        selectedRows={selectedRows}
        data={exportData}
        onFetchAllData={fetchAllPractitioners}
      />
    </div>
  );
}
